import {
  FC,
  memo,
  useCallback,
  Fragment,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from '@crew/modules/i18n'
import { Menu, Transition } from '@headlessui/react'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import MoreHoriz from '~icons/material-symbols/more-horiz'
import classNames from 'classnames'
import { debounce } from 'lodash'

export const FavoriteProjectMenuActionType = {
  activityOrder: 'activityOrder',
  nameOrder: 'nameOrder',
} as const
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type FavoriteProjectMenuActionType =
  (typeof FavoriteProjectMenuActionType)[keyof typeof FavoriteProjectMenuActionType]

export type FavoriteProjectContextMenuProps = {
  onMenuItemClick?: (action: FavoriteProjectMenuActionType) => void
}

const MIN_SPACE_FOR_DOWNWARD = 100
const MIN_SPACE_FOR_LEFTWARD = 130

export const FavoriteProjectContextMenu: FC<FavoriteProjectContextMenuProps> =
  memo((props) => {
    const { t } = useTranslation()

    const [contextMenuStyle, setContextMenuStyle] =
      useState<React.CSSProperties>()

    // アクティビティ順クリック時の処理
    const handleMenuItemActivityOrderClick = useCallback(() => {
      props.onMenuItemClick?.(FavoriteProjectMenuActionType.activityOrder)
    }, [props])

    // 名前順クリック時の処理
    const handleMenuItemNameOrderClick = useCallback(() => {
      props.onMenuItemClick?.(FavoriteProjectMenuActionType.nameOrder)
    }, [props])

    const buttonRef = useRef<HTMLButtonElement>(null)

    // コンテキストメニューのポジションを調整する
    useLayoutEffect(() => {
      const updateStyle = () => {
        if (!buttonRef.current) return

        const buttonRect = buttonRef.current?.getBoundingClientRect()
        const screenHeight = window.innerHeight
        const screenWidth = window.innerWidth

        // ----- 垂直方向の位置決定（上向きか下向きか）
        // ボタンの上下に利用可能なスペースを計算
        const spaceAbove = buttonRect.top
        const spaceBelow = screenHeight - buttonRect.bottom

        // 下向きに開くスペースが不足していて、かつ上側のスペースの方が広い場合は上向きに開く
        const shouldOpenUpward =
          spaceBelow < MIN_SPACE_FOR_DOWNWARD && spaceAbove > spaceBelow

        // ----- 水平方向の位置決定（左向きか右向きか）
        // ボタンの左右に利用可能なスペースを計算
        const spaceLeft = buttonRect.left
        const spaceRight = screenWidth - buttonRect.right

        // 左側のスペースが不足していて、かつ右側の方が広い場合は右向きに開く
        const shouldOpenRightward =
          spaceLeft < MIN_SPACE_FOR_LEFTWARD && spaceRight > spaceLeft

        // ----- 垂直方向と水平方向の配置に応じてスタイルを設定
        const style: React.CSSProperties = {}

        // 垂直方向の配置
        if (shouldOpenUpward) {
          // 上向きの場合、メニューの下端をボタンの上端に合わせる
          style.bottom = screenHeight - buttonRect.top
        } else {
          // 下向きの場合、メニューの上端をボタンの下端に合わせる
          style.top = buttonRect.bottom
        }

        // 水平方向の配置
        if (shouldOpenRightward) {
          // 右向きの場合、メニューの左端をボタンの左端に合わせる
          style.left = buttonRect.left
        } else {
          // 左向きの場合、メニューの右端をボタンの右端に合わせる
          style.right = screenWidth - buttonRect.right
        }

        style.minWidth = 0

        setContextMenuStyle(style)
      }

      // リサイズの度に計算されないようにdebounceでラップする
      const debouncedUpdate = debounce(updateStyle, 100)

      // ウィンドウのリサイズ時にも判定を行うようにする
      window.addEventListener('resize', debouncedUpdate)

      // 初回レンダリング時の判定
      updateStyle()

      // クリーンアップ
      return () => {
        window.removeEventListener('resize', debouncedUpdate)
        debouncedUpdate.cancel()
      }
    }, [])

    return (
      <Menu as="div" className="relative inline-block text-left">
        {({ open }) => (
          <>
            <div>
              <Menu.Button
                className="flex items-center rounded-md"
                onClick={(e) => e.stopPropagation()} // アコーディオンの開閉を防ぐ
              >
                <CrewButton
                  ref={buttonRef}
                  icon={<MoreHoriz width={20} height={20} />}
                  type="normal"
                  stylingMode="text"
                  className="hover:bg-transparent focus:bg-transparent"
                  size="sm"
                />
              </Menu.Button>
            </div>

            {contextMenuStyle && (
              <Transition
                as="div"
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                {createPortal(
                  <Menu.Items
                    static
                    className={
                      'fixed font-normal z-50 w-36 rounded-md crew-bg-default crew-text-default shadow-lg ring-1 ring-crew-gray-200 dark:ring-crew-gray-700 focus:outline-none'
                    }
                    style={contextMenuStyle}
                  >
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <span
                            className={classNames(
                              { 'crew-bg-gray-2': active },
                              'block px-4 py-2 text-sm cursor-pointer'
                            )}
                            onClick={handleMenuItemActivityOrderClick}
                          >
                            {t('label.activityOrder')}
                          </span>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <span
                            className={classNames(
                              { 'crew-bg-gray-2': active },
                              'block px-4 py-2 text-sm cursor-pointer'
                            )}
                            onClick={handleMenuItemNameOrderClick}
                          >
                            {t('label.nameOrder')}
                          </span>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>,
                  document.body
                )}
              </Transition>
            )}
          </>
        )}
      </Menu>
    )
  })
